// table of data, including paging

import React, { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import __isEmpty from 'lodash/isEmpty';
import __isEqual from 'lodash/isEqual';
import './Table.scss';
import ReactPaginate from 'react-paginate';
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';

const Table = ({
  columns,
  data = [],
  getRowProps = () => ({}),
  getHeaderProps = () => ({}),
  renderRowSubComponent = () => null,
  showPagination = false,
  selectedPrimaryRow = -1,
  selectedPageSize = 10,
  expandedRows = [],
  customPageCount,
  totalDataCount = null,
  onNextClick,
  sortedCols = [],
  resetTablePages,
  setResetTablePages,
  entryStartTimeIndex,
  entryEndTimeIndex,
  paginationOpts,
  tableIndex,
  setTableIndex,
}) => {
  // State initialization with stable defaults
  const [openedRows, setOpenedRows] = useState(() => expandedRows);
  const [sortedColumns, setSortedColumns] = useState(() => sortedCols);

  // Memoized table options to prevent unnecessary recreations
  const tableOptions = useMemo(() => ({
    columns,
    data,
    initialState: {
      pageIndex: 0,
      expanded: expandedRows,
      sortBy: sortedCols,
    },
    manualPagination: !!onNextClick,
    pageCount: Math.max(customPageCount || 1, 1),
  }), [columns, data, expandedRows, sortedCols, onNextClick, customPageCount]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    flatColumns,
    state: { pageSize },
    page,
    gotoPage, // eslint-disable-line no-unused-vars
    setPageSize,
  } = useTable(
    tableOptions,
    useSortBy,
    useExpanded,
    usePagination
  );

  // Effect for page size changes
  useEffect(() => {
    if (pageSize !== selectedPageSize) {
      let top_of_page = pageSize * tableIndex + 1;
      setPageSize(selectedPageSize);
      setTableIndex(Math.floor(top_of_page / selectedPageSize));
    }
  }, [selectedPageSize, pageSize, setPageSize]);

  // Effect for expanded rows changes
  useEffect(() => {
    if (!__isEqual(expandedRows, openedRows)) {
      setOpenedRows(expandedRows);
      // console.log(data); DEBUG
    }
  }, [expandedRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect for sorted columns changes
  useEffect(() => {
    if (!__isEmpty(sortedCols) && !__isEqual(sortedCols, sortedColumns)) {
      setSortedColumns(sortedCols);
    }
  }, [sortedCols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect for resetting pages
  useEffect(() => {
    if (resetTablePages) {
      setTableIndex(0);
      setResetTablePages(false);
    }
  }, [resetTablePages, setResetTablePages]);

  // Calculate display ranges
  const maxEntriesOnCurrentPage = pageSize * (tableIndex + 1);
  const currentItemStart = totalDataCount === 0 ? 0 : pageSize * tableIndex + 1;
  const displayCount = Math.min(maxEntriesOnCurrentPage, totalDataCount || 0);

  // Helper to safely extract props without key
  const getSafeProps = useCallback((props) => {
    if (!props) return {};
    const { key, ...rest } = props;
    return rest;
  }, []);

  return (
    <>
      <div className="weather-display-table">
        <div className="table" {...getSafeProps(getTableProps())}>
          <div className="thead">
            {headerGroups.map((headerGroup) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              return (
                <div 
                  key={headerGroupProps.key}
                  className="tr"
                  {...getSafeProps(headerGroupProps)}
                >
                  {headerGroup.headers.map((column) => {
                    const headerProps = column.getHeaderProps([
                      column.getSortByToggleProps(),
                      getHeaderProps(column),
                    ]);
                    return (
                      <div
                        key={headerProps.key}
                        className={`th ${column.className}`}
                        {...getSafeProps(headerProps)}
                      >
                        {column.render('Header')}
                        <span className="sort-icon">
                          {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div className="tbody" {...getSafeProps(getTableBodyProps())}>
            {page.map((row) => {
              prepareRow(row);
              const rowProps = row.getRowProps(getRowProps(row));
              
              return (
                <React.Fragment key={rowProps.key}>
                  <div
                    {...getSafeProps(rowProps)}
                    className={`tr
                      ${row.isExpanded ? 'expanded' : ''}
                      ${selectedPrimaryRow === row.index ? 'selected' : ''}
                    `}
                  >
                    {row.cells.map((cell) => {
                      const cellProps = cell.getCellProps();
                      return (
                        <div
                          key={cellProps.key}
                          className="td"
                          {...getSafeProps(cellProps)}
                        >
                          {cell.render('Cell')}
                        </div>
                      );
                    })}
                  </div>
                  {row.isExpanded && (
                    <div 
                      key={`expanded-${row.id}`}
                      className="tr sub-row"
                    >
                      <div className="td" colSpan={flatColumns?.length || 1}>
                        {renderRowSubComponent({ row })}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      {showPagination && (
        <div className="table-pagination-wrapper">
          <div className="table-page-info">
            Showing {currentItemStart} to {displayCount} of {totalDataCount} entries
          </div>
          <ReactPaginate {...paginationOpts} />
        </div>
      )}
    </>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  getRowProps: PropTypes.func,
  getHeaderProps: PropTypes.func,
  renderRowSubComponent: PropTypes.func,
  showPagination: PropTypes.bool,
  selectedPrimaryRow: PropTypes.number,
  selectedPageSize: PropTypes.number,
  expandedRows: PropTypes.array,
  customPageCount: PropTypes.number,
  totalDataCount: PropTypes.number,
  onNextClick: PropTypes.func,
  sortedCols: PropTypes.array,
  resetTablePages: PropTypes.bool,
  setResetTablePages: PropTypes.func,
};

export default Table;